/**
 * Open inquiries per area for the queue's entity chips ("UK 17 · USL 156 · DE 234",
 * invitee review MA-02 / MK-02, 12 Oct 2026).
 *
 * One `GET /queue` count per area of the viewer (one row asked, only the total read),
 * with the same filters a click on the chip applies: the "To review" tab, everyone's
 * cases, every received date, the viewer's mailboxes. The number on the chip is then
 * the number of rows the click shows. `/metrics/workload` would do it in one call for
 * managers, but it counts by owner and is not open to a sales viewer of many areas.
 *
 * Ease round (13 Oct 2026): every count call costs the server more than a second, so the
 * counts never run in front of the rows. They start when the rows are on the page and
 * the browser was idle once (`useWhenIdle`), and a count is kept for five minutes. One
 * count route on the server would replace the calls; that is an open finding (E-14).
 */

import { useQueries, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { type AreaInfo, fetchQueuePage, type QueueFilters } from "@/lib/api";

/** How long a chip count may be reused before it is asked again. */
export const COUNT_STALE_MS = 5 * 60_000;

/** The longest the counts wait for an idle browser after the rows are on the page. */
export const COUNT_IDLE_TIMEOUT_MS = 2000;

export interface AreaOpenCount {
  code: string;
  label: string;
  /** Null while loading or after a failed count. */
  open: number | null;
}

/** The filters of one chip: the area added to the shared count filters. */
export function areaCountFilters(base: QueueFilters, code: string): QueueFilters {
  return { ...base, view: "work", assignee: "any", area: [code], limit: 1, offset: 0 };
}

/** The filters of the chip "From colleagues": open mails that colleagues forwarded. */
export function colleaguesCountFilters(base: QueueFilters): QueueFilters {
  return {
    ...base,
    view: "work",
    assignee: "any",
    customer_role: ["norican_colleague"],
    include_internal: true,
    limit: 1,
  };
}

/**
 * True once `on` held and the browser was idle after that (at the latest
 * `COUNT_IDLE_TIMEOUT_MS` later). It stays true while `on` is true: the counts of a
 * later filter change are asked at once, the rows of the first load came first.
 */
export function useWhenIdle(on: boolean): boolean {
  const [idle, setIdle] = useState(false);
  useEffect(() => {
    if (!on || idle) {
      return;
    }
    if (typeof window.requestIdleCallback === "function") {
      const handle = window.requestIdleCallback(() => setIdle(true), {
        timeout: COUNT_IDLE_TIMEOUT_MS,
      });
      return () => window.cancelIdleCallback(handle);
    }
    const timer = window.setTimeout(() => setIdle(true), 200);
    return () => window.clearTimeout(timer);
  }, [on, idle]);
  return on && idle;
}

/**
 * The open count of every listed area, in the order of `/areas`. Nothing is asked while
 * `enabled` is false (one area, a tab without a backlog, the rows not on the page yet).
 */
export function useAreaOpenCounts(
  areas: readonly AreaInfo[],
  base: QueueFilters,
  enabled: boolean,
): AreaOpenCount[] {
  const results = useQueries({
    queries: areas.map((area) => {
      const filters = areaCountFilters(base, area.code);
      return {
        queryKey: ["queue", filters],
        queryFn: () => fetchQueuePage(filters),
        enabled,
        retry: false,
        staleTime: COUNT_STALE_MS,
      };
    }),
  });
  return areas.map((area, index) => ({
    code: area.code,
    label: area.label?.trim() || area.code.toUpperCase(),
    open: results[index]?.data?.total ?? null,
  }));
}

/**
 * The number on the chip "From colleagues": one count call, kept as long as an area
 * count and asked under the same condition. 0 while unknown.
 */
export function useColleaguesOpenCount(base: QueueFilters, enabled: boolean): number {
  const filters = colleaguesCountFilters(base);
  const { data } = useQuery({
    queryKey: ["queue", filters],
    queryFn: () => fetchQueuePage(filters),
    enabled,
    retry: false,
    staleTime: COUNT_STALE_MS,
  });
  return data?.total ?? 0;
}

/** "UK 17", "DE (WGG) 234": the chip text; the count is blank while unknown. */
export function areaChipText(entry: AreaOpenCount, nf: Intl.NumberFormat): string {
  return entry.open == null
    ? entry.code.toUpperCase()
    : `${entry.code.toUpperCase()} ${nf.format(entry.open)}`;
}
